import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "./auth";
import { fetchMyRidden, toggleMyRidden } from "./api";

/**
 * Ridden tracker per route:
 *  - Guests: persisted in localStorage under `mri:ridden:{slug}` (cross-tab synced)
 *  - Authed: persisted server-side, loaded once per session
 * Waypoint keys are stable: `${name}@${lat.toFixed(4)},${lng.toFixed(4)}`
 */

const STORAGE_KEY = (slug) => `mri:ridden:${slug}`;

export const waypointKey = (w) =>
    `${(w.name || "unnamed").trim()}@${Number(w.lat).toFixed(4)},${Number(w.lng).toFixed(4)}`;

const readLS = (slug) => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY(slug));
        return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
        return new Set();
    }
};

const writeLS = (slug, set) => {
    try {
        localStorage.setItem(STORAGE_KEY(slug), JSON.stringify(Array.from(set)));
    } catch {}
};

export const useRidden = (slug) => {
    const { isAuthed, user } = useAuth();
    const [set, setSet] = useState(() => (typeof window === "undefined" ? new Set() : readLS(slug)));

    // Refresh from correct source when slug or auth state changes
    useEffect(() => {
        let cancelled = false;
        if (isAuthed) {
            fetchMyRidden()
                .then((res) => {
                    if (cancelled) return;
                    const keys = new Set((res.ridden || {})[slug] || []);
                    setSet(keys);
                })
                .catch(() => {
                    if (!cancelled) setSet(readLS(slug));
                });
        } else {
            setSet(readLS(slug));
        }
        return () => {
            cancelled = true;
        };
    }, [slug, isAuthed, user?.user_id]);

    // Cross-tab sync (guests only)
    useEffect(() => {
        if (isAuthed || typeof window === "undefined") return;
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY(slug)) setSet(readLS(slug));
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [slug, isAuthed]);

    const toggle = useCallback(
        (key) => {
            // Optimistic local update
            setSet((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                if (!isAuthed) writeLS(slug, next);
                return next;
            });
            if (isAuthed) {
                // Fire-and-forget server sync; on failure, roll back
                toggleMyRidden(slug, key).catch(() => {
                    setSet((prev) => {
                        const rollback = new Set(prev);
                        if (rollback.has(key)) rollback.delete(key);
                        else rollback.add(key);
                        return rollback;
                    });
                });
            }
        },
        [slug, isAuthed]
    );

    const clear = useCallback(() => {
        setSet(new Set());
        if (!isAuthed) writeLS(slug, new Set());
        else {
            // Toggle-off each currently ridden one server-side
            Array.from(set).forEach((k) => {
                toggleMyRidden(slug, k).catch(() => {});
            });
        }
    }, [slug, isAuthed, set]);

    const isRidden = useCallback((key) => set.has(key), [set]);

    return useMemo(
        () => ({ ridden: set, count: set.size, toggle, clear, isRidden }),
        [set, toggle, clear, isRidden]
    );
};
